/**
 *
 */
import { isRankedIntent, isSponsoredIntent } from '@shared/types/types.ts';
import type { Product } from '@shared/types/types.ts';
import type { TileListPageFetcher } from '@tilelist/application/ports/TileListPageFetcher.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import {
    BLACK_FRIDAY_SLIDER_MIN_PRODUCTS,
    COLLAPSED_BLACK_FRIDAY_SLIDER,
    isSponsoredBlackFridayActive,
    toBlackFridayProductsQuery,
    toOrganicBlackFridaySlider,
    toSponsoredBlackFridaySlider,
} from '@tilelist/domain/black-friday-slider/BlackFridaySlider.ts';
import { toProductItem } from '@tilelist/domain/composition/RankedPositioning.ts';
import type { BlackFridaySliderItem, ProductItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { DEFAULT_ONEX } from '@tilelist/domain/experiments/OnExAdapter.ts';

/*                                                                                                           */
const BLACK_FRIDAY_SLIDER_FEATURE_HEADERS = { 'X-Feature': 'blackFridaySlider@RepTile-Dundee' };

export class BlackFridaySliderLoader {
    constructor(
        private readonly pageFetcher: TileListPageFetcher,
        private readonly variationLoader: Pick<VariationLoader, 'loadBestVariationsForFirstNProducts'>,
        private readonly onex: OnEx = DEFAULT_ONEX,
    ) {}

    /*                                                                                                         */
    async load(blackFridayRule: string | undefined): Promise<BlackFridaySliderItem> {
        if (!blackFridayRule) return COLLAPSED_BLACK_FRIDAY_SLIDER;

        const result = await this.pageFetcher.getProducts(
            toBlackFridayProductsQuery(blackFridayRule, this.onex),
            BLACK_FRIDAY_SLIDER_FEATURE_HEADERS,
        );
        if (result.state === 'error') return COLLAPSED_BLACK_FRIDAY_SLIDER;

        const products = result.payload.intents.find(isRankedIntent)?.products ?? [];
        if (products.length < BLACK_FRIDAY_SLIDER_MIN_PRODUCTS) {
            return COLLAPSED_BLACK_FRIDAY_SLIDER;
        }

        try {
            /*                                                                  */
            if (!isSponsoredBlackFridayActive(this.onex)) {
                return toOrganicBlackFridaySlider(await this.enrich(products));
            }
            const sponsoredProducts = result.payload.intents.find(isSponsoredIntent)?.products ?? [];
            const [organic, sponsored] = await Promise.all([this.enrich(products), this.enrich(sponsoredProducts)]);
            return toSponsoredBlackFridaySlider(organic, sponsored);
        } catch {
            return COLLAPSED_BLACK_FRIDAY_SLIDER;
        }
    }

    private async enrich(products: Product[]): Promise<ProductItem[]> {
        if (products.length === 0) return [];
        const items = products.map((product, index) =>
            toProductItem({ product, interleavedIndex: index, startingOffset: 0, originPosition: index }),
        );
        return this.variationLoader.loadBestVariationsForFirstNProducts(items, items.length);
    }
}
